/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  Injector,
  input,
  model,
  OnInit,
  Signal,
  viewChild,
} from '@angular/core';
import { AbstractControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import type { PickerType } from './datepicker.interface';
import { DatePipe } from '@angular/common';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { CustomValidators } from '../../object-editor/pattern.validator';
import { AbstractFormInputDirective } from '../../../../lib/components/abstract-form-input.directive';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormErrorsComponent } from '../../../../lib/components/form-errors/form-errors.component';
import { DateTime } from 'luxon';
import { TranslatePipe } from '@ngx-translate/core';

function toDate(value: Date | number | null | undefined): Date | null {
  return typeof value === 'number' ? new Date(value) : (value ?? null);
}

export const DATEPICKER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatepickerComponent),
  multi: true,
};

@Component({
  selector: 'vitamui-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  providers: [DATEPICKER_VALUE_ACCESSOR],
  imports: [
    FormErrorsComponent,
    FormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class DatepickerComponent extends AbstractFormInputDirective implements OnInit {
  private readonly datePipe = inject(DatePipe);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly pickerType = input<PickerType>('day');
  // May be yyyy-MM-dd, dd/MM/yyyy, yyyy or any other date format
  format = input<string>();
  readonly startView = model<MatDatepicker<Date>['startView']>(undefined);
  readonly label = input('DATE.DATE');
  readonly hint = input<string>(undefined);
  // Also accept a timestamp (number), as set by [formField] from the field min/max metadata
  readonly min = input<Date | number | null>(undefined);
  readonly max = input<Date | number | null>(undefined);
  protected readonly minDate = computed(() => toDate(this.min()));
  protected readonly maxDate = computed(() => toDate(this.max()));
  readonly outputType = input<'String' | 'Date'>('String');

  // We store a value specific for the datepicker to store a Date object and not a String for datepicker to keep the currently selected value
  datePickerValue: Date;
  displayedValue = '';

  @HostBinding('class.vitamui-float')
  get labelFloat(): boolean {
    return !!this.displayedValue;
  }

  private readonly vitamUIInput = viewChild<ElementRef>('vitamUIInput');
  private readonly datepicker = viewChild<MatDatepicker<Date>>('datepicker');
  private readonly hintArea = viewChild<ElementRef>('hintArea');

  @HostListener('click', ['$event.target'])
  onClick(target: EventTarget) {
    if (!this.hintArea().nativeElement.contains(target as Node)) {
      this.vitamUIInput().nativeElement.focus();
    }
  }

  private readonly startViewMapping: Map<PickerType, MatDatepicker<Date>['startView']> = new Map([
    ['year', 'multi-year'],
    ['month', 'year'],
    ['day', 'month'],
  ]);

  private readonly defaultFormatMapping = new Map<PickerType, string>([
    ['day', 'yyyy-MM-dd'],
    ['month', 'yyyy-MM'],
    ['year', 'yyyy'],
  ]);

  // Declared as a field (and not in ngOnInit) because writeValue can be called before ngOnInit (e.g. with [formField])
  readonly selectedFormat: Signal<string> = computed(() => this.format() || this.defaultFormatMapping.get(this.pickerType()));

  constructor() {
    const injector = inject(Injector);

    super(injector);
  }

  override ngOnInit() {
    super.ngOnInit();

    if (!this.startView()) this.startView.set(this.startViewMapping.get(this.pickerType()));
    this.control.addValidators((control: AbstractControl<Date | string>) => {
      const customControl = this.outputType() === 'Date' ? ({ value: this.formatDateToString(control.value) } as AbstractControl) : control;
      return CustomValidators.date(this.selectedFormat())(customControl);
    });
  }

  override writeValue(value: string | Date) {
    this.displayedValue = this.formatDateToString(value);
    this.datePickerValue = value ? (value instanceof Date ? value : DateTime.fromFormat(value, this.selectedFormat()).toJSDate()) : null;
    // writeValue may be called outside of change detection (zoneless)
    this.cdr.markForCheck();
  }

  onFocus() {
    if (!this.control.disabled) {
      this.onTouched();
    }
  }

  onBlur() {
    this.onTouched();
    if (this.displayedValue !== (this.outputType() === 'Date' ? this.formatDateToString(this.control.value) : this.control.value)) {
      const parsed = DateTime.fromFormat(this.displayedValue, this.selectedFormat());

      if (parsed.isValid) {
        this.changeValue(parsed.toJSDate());
      } else {
        this.changeValue(null);
      }
    }
  }

  onTextChange(value: string) {
    this.displayedValue = value;
    if (DateTime.fromFormat(value, this.selectedFormat()).isValid) {
      const valueWithSlash = value.replaceAll('-', '/'); // We must use '/' to take timezone into account ('-' doesn't)
      const endsWithYear = /\d{4,}$/.test(value);
      const normalized = endsWithYear ? valueWithSlash.split('/').reverse().join('/') : valueWithSlash;
      this.changeValue(new Date(normalized));
    } else {
      this.changeValue(null);
    }
  }

  onDateChange(type: PickerType, date: Date) {
    if (this.pickerType() === type) {
      this.changeValue(date);
      const datepicker = this.datepicker();
      if (datepicker.opened) datepicker.close();
    }
  }

  private formatDateToString(date: Date | string): string {
    return date instanceof Date ? this.datePipe.transform(date, this.selectedFormat()) : date;
  }

  private changeValue(date: Date) {
    this.datePickerValue = date;
    if (date) this.displayedValue = this.formatDateToString(date);
    const typedDate: string | Date = this.outputType() === 'Date' ? date : this.displayedValue;
    this.control.setValue(typedDate);
    this.onChange(typedDate);
  }
}
